import React, { useState, useEffect } from 'react';
import SearchForm from './components/SearchForm';
import CommentList from './components/CommentList';
import RacerProfile from './components/RacerProfile';
import SavedRacersList from './components/SavedRacersList';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { searchRacerComments } from './services/api';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles.css';

function App() {
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [racerProfile, setRacerProfile] = useState(null);
  
  // 保存した選手データを格納するステート
  const [savedRacers, setSavedRacers] = useState({});

  // LocalStorageからデータを読み込む
  useEffect(() => {
    const savedRacersData = localStorage.getItem('savedRacers');
    if (savedRacersData) {
      try {
        setSavedRacers(JSON.parse(savedRacersData));
      } catch (e) {
        console.error('LocalStorage data parse error:', e);
      }
    }
  }, []);

  // savedRacersが更新されたらLocalStorageに保存
  useEffect(() => {
    if (Object.keys(savedRacers).length > 0) {
      localStorage.setItem('savedRacers', JSON.stringify(savedRacers));
    } else {
      // キャッシュが空の場合はlocalStorageから削除
      localStorage.removeItem('savedRacers');
    }
  }, [savedRacers]);

  // 選手情報を保存する関数
  const saveRacerData = (racerNumber, racerInfo, racerComments) => {
    if (!racerInfo || !racerComments || racerComments.length === 0) return;

    setSavedRacers(prev => ({
      ...prev,
      [racerNumber]: {
        profile: racerInfo,
        comments: racerComments,
        lastAccessed: new Date().toISOString()
      }
    }));
  };

  // 保存済み選手データの表示
  const loadSavedRacerData = (racerNumber) => {
    if (!savedRacers[racerNumber]) return;

    const { profile, comments } = savedRacers[racerNumber];
    
    // アクセス日時を更新
    setSavedRacers(prev => ({
      ...prev,
      [racerNumber]: {
        ...prev[racerNumber],
        lastAccessed: new Date().toISOString()
      }
    }));

    setSearchTerm(racerNumber);
    setRacerProfile(profile);
    setComments(comments);
    setSearchPerformed(true);
    setError(null);
  };
  
  // 個別の選手データを削除する関数
  const deleteRacerData = (racerNumber, event) => {
    // イベントの伝播を停止（親要素のクリックイベントを発火させない）
    if (event) {
      event.stopPropagation();
    }
    
    // 現在表示中の選手が削除対象の場合は表示をクリア
    if (racerProfile && racerProfile.racer_number === racerNumber) {
      setRacerProfile(null);
      setComments([]);
      setSearchPerformed(false);
    }
    
    // 対象の選手を除いた新しいオブジェクトを作成
    const newSavedRacers = { ...savedRacers };
    delete newSavedRacers[racerNumber];
    setSavedRacers(newSavedRacers);
  };
  
  // 全ての保存済み選手データを削除する関数
  const clearAllRacerData = () => {
    // 全てのデータをクリア
    setSavedRacers({});
    
    // 現在表示中のデータもクリア
    if (racerProfile) {
      setRacerProfile(null);
      setComments([]);
      setSearchPerformed(false);
    }
  };

  // 検索処理を実行する関数
  const handleSearch = async (term) => {
    if (!term.trim()) {
      setError('選手番号を入力してください');
      return;
    }
    
    // 選手番号は数字のみであることを検証
    if (!/^\d+$/.test(term)) {
      setError('選手番号は半角数字のみを入力してください');
      return;
    }
    
    // 既にキャッシュされているデータがあれば、それを使用
    if (savedRacers[term]) {
      loadSavedRacerData(term);
      return;
    }

    setSearchTerm(term);
    setIsLoading(true);
    setError(null);
    setSearchPerformed(true);
    setRacerProfile(null); // プロフィールをリセット
    setComments([]); // 検索前に結果をクリア
    
    try {
      console.log(`検索開始: ${term}`); // デバッグログ
      
      // 実際のAPIを呼び出す
      const result = await searchRacerComments(term);
      
      console.log('検索結果:', result); // デバッグログ
      
      // API返却値からコメントと選手情報を設定
      if (result.comments && result.comments.length > 0) {
        setComments(result.comments);
        setRacerProfile(result.racerInfo); // API返却の選手情報を直接使用
        
        // 選手情報が取得できた場合、キャッシュに保存
        saveRacerData(term, result.racerInfo, result.comments);
      } 
    } catch (err) {
      console.error('検索エラー:', err);
      setError(`検索中にエラーが発生しました: ${err.message}`);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="py-4">
      <header className="text-center mb-5">
        <h1>平和島競艇ライブレポート過去コメント検索システム（非公式）</h1>
        <p className="text-muted">選手番号を入力して過去のコメントを検索できます</p>
      </header>

      {/* 検索フォーム - 常に一番上 */}
      <Row className="justify-content-center mb-4">
        <Col md={8}>
          <SearchForm onSearch={handleSearch} />
        </Col>
      </Row>
      
      {/* 保存された選手リスト - 検索フォームの下、結果の上 */}
      <Row className="mb-4">
        <Col md={12} lg={8} className="mx-auto">
          <SavedRacersList 
            racers={savedRacers} 
            onSelectRacer={loadSavedRacerData} 
            onDeleteRacer={deleteRacerData}
            onClearAll={clearAllRacerData}
            currentRacerId={racerProfile?.racer_number}
          />
        </Col>
      </Row>
      
      {/* 検索結果と選手情報 - 常に下部 */}
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          {isLoading && (
            <div className="text-center my-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">読み込み中...</span>
              </div>
              <p className="mt-2">データを取得中...</p>
            </div>
          )}

          {error && (
            <Alert variant="danger" className="my-3">
              {error}
            </Alert>
          )}

          {searchPerformed && !isLoading && comments.length === 0 && (
            <Alert variant="danger" className="text-center">
              選手番号「{searchTerm}」の検索結果はありません。選手番号を正確に入力してください。
            </Alert>
          )}

          {comments.length > 0 && (
            <>
              <h2 className="text-center mb-4">
                「{racerProfile ? racerProfile.name : searchTerm}」の過去コメント（{comments.length}件）
              </h2>
              {/* 選手プロフィールカード - 中央寄せ */}
              {racerProfile && (
                <div className="d-flex justify-content-center mb-4">
                  <RacerProfile profile={racerProfile} />
                </div>
              )}
              
              <CommentList comments={comments} />
            </>
          )}
        </Col>
      </Row>

      <footer className="text-center mt-5 pt-3 border-top text-muted">
        <p>このサイトは非公式ファンサイトです。正しい情報は、公式サイトをご確認ください</p>
      </footer>
    </Container>
  );
}

export default App;